import React, { useState } from 'react';

import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { MoreHorizontal } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

const ROW_MENU_CLASS = 'assignee-row-menu';

/** Spread into an assignee row's styles so its "..." shows while the row is hovered. */
export const ASSIGNEE_ROW_MENU_HOVER = {
  [`&:hover .${ROW_MENU_CLASS}`]: { opacity: 1 },
};

const useStyles = makeStyles()(theme => ({
  button: {
    '&:focus-visible': { opacity: 1 },
    // Touch screens can't hover, so the menu stays in view there.
    '@media (hover: none)': { opacity: 1 },
    flexShrink: 0,
    margin: theme.spacing(-0.5, 0),
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  buttonOpen: {
    opacity: 1,
  },
  destructive: {
    color: theme.palette.error.main,
  },
  icon: {
    color: 'inherit',
    minWidth: 28,
  },
}));

export interface AssigneeRowMenuItem {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  /** Shown as the tooltip of a disabled item. */
  disabledReason?: string | null;
  isDestructive?: boolean;
}

/** The "..." at the end of an assignee row, holding the actions that row allows. */
export const AssigneeRowMenu = React.memo(function AssigneeRowMenu({
  items,
  name,
}: {
  items: readonly AssigneeRowMenuItem[];
  name: string;
}) {
  const { classes, cx } = useStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-haspopup="menu"
        aria-label={`More actions for ${name}`}
        className={cx(classes.button, ROW_MENU_CLASS, Boolean(anchor) && classes.buttonOpen)}
        disabled={!items.length}
        size="small"
        onClick={event => setAnchor(event.currentTarget)}
      >
        <MoreHorizontal size={16} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={Boolean(anchor)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setAnchor(null)}
      >
        {items.map(item => (
          <AlineTooltip key={item.label} arrow placement="left" title={item.disabledReason ?? ''}>
            <span>
              <MenuItem
                dense
                className={item.isDestructive ? classes.destructive : undefined}
                disabled={Boolean(item.disabledReason)}
                onClick={() => {
                  setAnchor(null);
                  item.onClick();
                }}
              >
                <ListItemIcon className={classes.icon}>
                  <item.icon size={16} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            </span>
          </AlineTooltip>
        ))}
      </Menu>
    </>
  );
});
